import moment from 'moment';
import { isLoggedIn } from '../User/AuthActions';
import { getInstitutionsRequest } from '../Institution/InstitutionActions';
import { getConversationsRequest } from '../Conversation/ConversationActions';
import { getConfigRequest } from '../Config/ConfigActions';
import { addError } from '../Error/ErrorActions';

import config from '../../config';

import callApi from '../../util/apiCaller';
import { sequence } from '../../util/promises';
import { getHashKeys } from '../../util/security';
import { initLanguage } from '../Intl/IntlActions';

// Export Constants
export const INIT_SOCKET = 'INIT_SOCKET';
export const SEND_SOCKET = 'SEND_SOCKET';
export const RECEIVE_SOCKET = 'RECEIVE_SOCKET';
export const DISCONNECT_SOCKET = 'DISCONNECT_SOCKET';
export const SET_REDIRECT = 'SET_REDIRECT';
export const REDIRECT = 'REDIRECT';
export const SET_MOUNTED = 'SET_MOUNTED';
export const SET_APP_UPDATE = 'SET_APP_UPDATE';
export const UPDATE_TABLE = 'UPDATE_TABLE';
export const INIT_PERSISTED_DATA = 'INIT_PERSISTED_DATA';
export const SET_IS_FETCHING = 'SET_IS_FETCHING';
export const ADD_CHANNEL = 'ADD_CHANNEL';
export const REMOVE_CHANNEL = 'REMOVE_CHANNEL';
export const SET_SUBSCRIBE_CHANNEL_NEED = 'SET_SUBSCRIBE_CHANNEL_NEED';
export const SET_ENVIRONMENT = 'SET_ENVIRONMENT';
export const SET_WINDOW_FOCUS = 'SET_WINDOW_FOCUS';
export const SET_APP_EMBEDDED = 'SET_APP_EMBEDDED';
export const ADD_SHORTCUT = 'ADD_SHORTCUT';
export const REMOVE_SHORTCUT = 'REMOVE_SHORTCUT';

export const FETCH_LIMIT = false && process.env.NODE_ENV === 'development' ? 10 : 100;

// Export Request Actions
export function initApp(isSSR = false, user = null) {
	!isSSR && console.log('App::: Init');
	return dispatch => {
		return (user ? Promise.resolve(user) : dispatch(isLoggedIn())).then(user => {
			if(user) {
				let initItems = [checkVersionRequest, checkServerTimeRequest, () => initLanguage(null, user), getAppCguUpdateConfig, /* getApplicantsRequest, */ getConversationsRequest];

				switch(user.role) {
				case 'root':
				case 'admin':
				case 'manager':
				case 'professor':
					initItems = initItems.concat([() => getInstitutionsRequest(true)]); /* , getUsersRequest */
					break;

				case 'redactor':
				case 'proctor':
				case 'corrector':
					initItems = initItems.concat([() => getInstitutionsRequest(true)]);
					break;

				default:
					initItems = initItems.concat([getInstitutionsRequest]);
					break;
				}

				// sequence(initItems, dispatch).then(results => {
				//     dispatch(setMounted());
				// });
				Promise.allSettled(initItems.map(initItem => dispatch(initItem()))).then(results => {
					dispatch(setMounted());
				});
			} else {
				if(!isSSR) {
					dispatch(setMounted());
				}
			}
		});
	};
}

export function checkVersionRequest(forceRefresh = false) {
	return dispatch => callApi(`app/web/version${forceRefresh ? '?refreshCache=1' : ''}`).then(res => {
		const needUpdate = res.version && res.version !== config.version && process.env.NODE_ENV !== 'development' && !process.env.SKIP_VERSION;
		// if(needUpdate)
		//     typeof window !== 'undefined' && window.location.reload();
		// }
		needUpdate && dispatch(setAppOnUpdate());
		return {
			version: res.version,
			needUpdate,
		};
	}).catch(error => {
		dispatch(addError(error));
	});
}

export function getAppCguUpdateConfig() {
	return getConfigRequest('app-cgu-update');
}

export function checkServerTimeRequest() {
	return dispatch => {
		// Only check time on client side
		if(typeof window !== 'undefined') {
			return callApi('app/time', 'post', { 'dateClient': moment.utc().format() }).then(res => {
				if(res && res.diffTime !== 0) {
					console.log('RESET base time', moment.now(), res.diffTime, `Edit with ${-(res.diffTime * 60 * 1000)} ms`);
					moment.now = function() {
						return Date.now() - (res.diffTime * 60 * 1000);
					};
				}
			}).catch(err => {
				console.error(err);
			});
		}
		return Promise.resolve();
	};
}

export function createIndexRequest() {
	return dispatch => callApi('app/database/index').then(res => {
		return res.ok;
	}).catch(error => {
		dispatch(addError(error));
	});
}

export function purgeCacheRequest() {
	return dispatch => callApi('app/cache/purge', 'post').then(res => {
		return res && res.ok;
	}).catch(error => {
		dispatch(addError(error));
		return false;
	});
}

export function getItemsRequest(request, itemsKey = '', onFinish = () => {}, params = {}, options = {}) {
	const {
		search = '', // 'look at this'
		filters = null, // { myField: 'my value', ...}
		sorter = null, // 'myField' OR { column: 'myField', type: 'ASC' } type => 'ASC' || 'DESC'
		pager = null, // { current: 2, size: 20 } OR 20 (as page size)
		extra = null, // Extra parameters added to request in GET ?param=abc
	} = params || {};
	// console.log(`App::: get items::: ${itemsKey} / ${request}`, search, filters, sorter, pager, extra);

	itemsKey = itemsKey || request;
	const recursiveFetch = (limit, start = 0, items = []) => {
		// console.log(`App::: get items::: Fetch from ${start} to ${start + limit - 1} ${itemsKey}`);
		return new Promise((resolve, reject) => {
			callApi(`${request}?${search ? `search=${search}&` : ''}${filters ? `${Object.keys(filters).map(filterKey => `filters[${filterKey}]=${filters[filterKey]}`).join('&')}&` : ''}${sorter ? `sortBy=${sorter.column || sorter}&sortType=${sorter.type || 'ASC'}` : ''}&start=${start}&limit=${limit}&${Object.keys(extra || {}).map(extraKey => `${extraKey}=${extra[extraKey]}`).join('&')}`).then(res => {
				if(res) {
					items = items.concat(res[itemsKey] || []);
					if(options.isRecursive && res[itemsKey] && res[itemsKey].length >= limit) {
						return resolve(recursiveFetch(limit, start + limit, items));
					}
					resolve(items);
				} else {
					reject(new Error('ItemsRequestError'));
				}
			}).catch(err => {
				reject(err);
			});
		});
	};

	return dispatch => {
		dispatch && dispatch(setIsFetching(itemsKey));
		const pageSize = (pager && pager.size) || pager || FETCH_LIMIT;
		return recursiveFetch(pageSize, pager ? (pager.current || 0) * pageSize : 0).then(items => {
			// console.log('App::: get items::: Found', items.length);
			onFinish && onFinish(items);
			dispatch && dispatch(setIsFetching(itemsKey, false));
			return items;
		}).catch(err => {
			dispatch && dispatch(addError(err));
			return null;
		});
	};
}

// Export Actions

export function getTable(store, tableId) {
	return store.app.tables.find(table => table.id === tableId) || {};
}

export function isFetching(store, type) {
	return (store[type] || {}).isFetching || false;
}

export function getEnvironment(store, type, key = null) {
	const data = store.app.environment[type] || null;
	if(data && key) {
		return data[key];
	}
	return data;
}

export function isAppUpdating(store) {
	return store.app.isUpdating;
}

export function isWindowFocus(store) {
	return store.app.isFocus;
}

export function isEmbedded(store) {
	return store.app.isEmbedded;
}

export function isCanonicalForced(store) {
	return store.app.forceCanonical;
}

export function getShortcuts(store) {
	return store.app.shortcuts || [];
}

export function getShortcut(store, type, id) {
	return (store.app.shortcuts || []).find(shortcut => shortcut.type === type && shortcut.id === id);
}

export function initSocket(socket) {
	return {
		type: INIT_SOCKET,
		socket,
	};
}

export function sendSocket(datas) {
	// Secure subscribe socket with key
	if(datas && datas.type === 'subscribe' && typeof datas.data === 'object') {
		datas.data = {
			...(datas.data || {}),
			...getHashKeys(datas.data.channel),
		};
	}
	return {
		type: SEND_SOCKET,
		data: datas,
	};
}

export function receiveSocket(datas) {
	return {
		type: RECEIVE_SOCKET,
		data: datas,
	};
}

export function disconnectSocket() {
	return {
		type: DISCONNECT_SOCKET,
	};
}

export function subscribeToChannel(channel, addToChannelsList = true) {
	return dispatch => {
		dispatch(sendSocket({ type: 'subscribe', data: { channel } }));
		addToChannelsList && dispatch(addChannelSubscription(channel));
	};
}

export function unsubscribeToChannel(channel, removeFromChannelsList = true) {
	return dispatch => {
		dispatch(sendSocket({ type: 'unsubscribe', data: { channel } }));
		removeFromChannelsList && dispatch(removeChannelSubscription(channel));
	};
}

export function setRedirect(redirect = '') {
	// console.log('setRedirect', redirect);
	return {
		type: SET_REDIRECT,
		redirect,
	};
}

export function redirect() {
	return {
		type: REDIRECT,
	};
}

export function setMounted() {
	return {
		type: SET_MOUNTED,
	};
}

export function updateTable(tableId, tableData) {
	return {
		type: UPDATE_TABLE,
		tableId,
		tableData,
	};
}

export function initPersistedData() {
	return {
		type: INIT_PERSISTED_DATA,
	};
}

export function setIsFetching(dataType, isFetching = true) {
	return {
		type: SET_IS_FETCHING,
		dataType: dataType.toLowerCase(),
		isFetching,
	};
}

export function addChannelSubscription(channel) {
	return {
		type: ADD_CHANNEL,
		channel,
	};
}

export function removeChannelSubscription(channel) {
	return {
		type: REMOVE_CHANNEL,
		channel,
	};
}

export function setResubscribeToChannel(need = true) {
	return {
		type: SET_SUBSCRIBE_CHANNEL_NEED,
		need,
	};
}

export function setEnvironment(environment) {
	return {
		type: SET_ENVIRONMENT,
		environment,
	};
}

export function setWindowFocus(isFocus = true) {
	return {
		type: SET_WINDOW_FOCUS,
		isFocus,
	};
}

export function setAppOnUpdate() {
	return {
		type: SET_APP_UPDATE,
	};
}

export function setEmbeddedMode(isEmbedded = true) {
	return {
		type: SET_APP_EMBEDDED,
		isEmbedded,
	};
}
export function addShortcut(type, id, label = '', reference = {}) {
	return {
		type: ADD_SHORTCUT,
		shortcut: {
			type,
			id,
			label,
			reference,
		},
	};
}

export function removeShortcut(shortcut) {
	return {
		type: REMOVE_SHORTCUT,
		shortcut,
	};
}
